// @ts-strict-ignore
import isDate from 'lodash/isDate';
import isString from 'lodash/isString';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import moment, {Moment} from 'moment';
// which can fail type checking if the date util mixes these with moment-timezone instead
import momentTimezone from 'moment-timezone';
import isUndefined from 'lodash/isUndefined';
import pickBy from 'lodash/pickBy';
import pick from 'lodash/pick';

import {CALENDAR_DATE_FORMAT, TIMESTAMP_FORMAT, Timezone} from 'common/constants';
import {TimePeriodSettings} from 'common/report/stores/types';
import {SUB_DAY_TIME_UNITS} from 'common/util/date';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {
  START_OF_MONTH,
  START_OF_QUARTER,
  START_OF_WEEK,
  START_OF_YEAR,
} from 'common/widgets/date-range-picker/models/presets';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {BaseTimeUnit, DateRangeType, RelativeDate} from 'common/types/reports/bookmark';

import {
  END_OF_TODAY,
  END_OF_YESTERDAY,
  START_OF_TODAY,
  START_OF_YESTERDAY,
  TIME_UNIT_QUERY_LIST,
  TIME_UNITS,
} from './constants';
import {ClauseType} from './types';

// Time constants
const SPECIAL_RANGES_SET = new Set([
  START_OF_YESTERDAY,
  END_OF_YESTERDAY,
  START_OF_TODAY,
  END_OF_TODAY,
  START_OF_WEEK,
  START_OF_MONTH,
  START_OF_QUARTER,
  START_OF_YEAR,
]);
const TODAY = `$today`;

const TIME_UNIT_LIST: TimeUnit[] = [TIME_UNITS.MINUTE, ...TIME_UNIT_QUERY_LIST];

export const RANGES = {
  TODAY: `Current day`,
  YESTERDAY: `Previous day`,
  HOURS: `Last 96 hours`,
  MONTH: `Last 30 days`,
  WEEKS: `Last 24 weeks`,
  YEAR: `Last 12 months`,
  CUSTOM: `Select a date range...`,
};

const RANGE_TO_VALUE_AND_UNIT = {
  [RANGES.TODAY]: {value: TODAY, unit: TIME_UNITS.HOUR},
  [RANGES.HOURS]: {value: 96, unit: TIME_UNITS.HOUR},
  [RANGES.MONTH]: {value: 30, unit: TIME_UNITS.DAY},
  [RANGES.WEEKS]: {value: 24, unit: TIME_UNITS.WEEK},
  [RANGES.YEAR]: {value: 12, unit: TIME_UNITS.MONTH},
};

const UNIT_AND_VALUE_TO_RANGE = {
  hour: {
    [TODAY]: RANGES.TODAY,
    96: RANGES.HOURS,
  },
  day: {
    30: RANGES.MONTH,
  },
  week: {
    24: RANGES.WEEKS,
  },
  month: {
    12: RANGES.YEAR,
  },
};

export const DATE_RANGE_TYPE = {
  BETWEEN: DateRangeType.Between,
  ON: DateRangeType.On,
  RELATIVE: DateRangeType.RelativeAfter,
  SINCE: DateRangeType.Since,
};

export const RANGE_TO_TIME_CLAUSE_PARAMS = {
  [RANGES.TODAY]: {
    dateRangeType: DATE_RANGE_TYPE.SINCE,
    value: START_OF_TODAY,
    window: null,
    unit: TIME_UNITS.HOUR,
  },
  [RANGES.YESTERDAY]: {
    dateRangeType: DATE_RANGE_TYPE.BETWEEN,
    value: [START_OF_YESTERDAY, END_OF_YESTERDAY],
    window: null,
    unit: TIME_UNITS.HOUR,
  },
  [RANGES.HOURS]: {
    dateRangeType: DATE_RANGE_TYPE.RELATIVE,
    value: null,
    window: {
      value: 96,
      unit: TIME_UNITS.HOUR,
    },
    unit: TIME_UNITS.HOUR,
  },
  [RANGES.MONTH]: {
    dateRangeType: DATE_RANGE_TYPE.RELATIVE,
    value: null,
    window: {
      value: 30,
      unit: TIME_UNITS.DAY,
    },
    unit: TIME_UNITS.DAY,
  },
  [RANGES.WEEKS]: {
    dateRangeType: DATE_RANGE_TYPE.RELATIVE,
    value: null,
    window: {
      value: 24,
      unit: TIME_UNITS.WEEK,
    },
    unit: TIME_UNITS.WEEK,
  },
  [RANGES.YEAR]: {
    dateRangeType: DATE_RANGE_TYPE.RELATIVE,
    value: null,
    window: {
      value: 12,
      unit: TIME_UNITS.MONTH,
    },
    unit: TIME_UNITS.MONTH,
  },
};
export const NUMBER_OF_DAYS_IN_UNIT = {
  [TimeUnit.Minute]: 1 / 1440,
  [TimeUnit.Hour]: 1 / 24,
  [TimeUnit.Day]: 1,
  [TimeUnit.Week]: 7,
  [TimeUnit.Month]: 30,
  [TimeUnit.Quarter]: 90,
  [TimeUnit.Year]: 365,
};
const NUMBER_OF_HOURS_IN_UNIT = {
  [TimeUnit.Minute]: 1 / 60,
  [TimeUnit.Hour]: 1,
  [TimeUnit.Day]: 24,
  [TimeUnit.Week]: NUMBER_OF_DAYS_IN_UNIT[TimeUnit.Week] * 24,
  [TimeUnit.Month]: NUMBER_OF_DAYS_IN_UNIT[TimeUnit.Month] * 24,
  [TimeUnit.Quarter]: NUMBER_OF_DAYS_IN_UNIT[TimeUnit.Quarter] * 24,
  [TimeUnit.Year]: NUMBER_OF_DAYS_IN_UNIT[TimeUnit.Year] * 24,
};

/**
 * Calculates the number of hours in a given time clause.
 *
 * @param {object} timeClause - The time clause to calculate hours for.
 * @param {string} projectTimezone - Timezone of the project. May be null if project context hasn't yet been initialized.
 * @param {string} projectAsOfDate - Optional as-of date of the project. Current time will be used if not provided.
 * @param {TimePeriodSettings} timePeriodSettings - Optional settings for the time period.
 *
 * @returns {number} The number of hours in the time clause.
 *
 * @example
 * getNumberOfHoursInTimeClause({value: 5, unit: TimeUnit.Hour});
 */
export function getNumberOfHoursInTimeClause(
  timeClause,
  projectTimezone: Timezone,
  projectAsOfDate?: string,
  timePeriodSettings?: TimePeriodSettings,
): number {
  if (isNumber(timeClause.value)) {
    // TODO(go/j/AE-495): Remove legacy time clause support and raise an error here instead.
    console.warn(`Unexpected legacy time clause format.`);

    // Moment diff uses floor to get the number of hours.
    // For consistency, we manually call `floor` since minute units can result in a fractional number of hours.
    return Math.floor(timeClause.value * NUMBER_OF_HOURS_IN_UNIT[timeClause.unit]);
  }

  let asOfDate: Moment;
  if (projectTimezone && projectAsOfDate) {
    asOfDate = momentTimezone.tz(projectAsOfDate, projectTimezone as string);
  } else if (projectTimezone) {
    asOfDate = momentTimezone().tz(projectTimezone as string);
  } else {
    // Fallback on local time if the projectTimezone is null.
    asOfDate = moment();
  }
  const [fromDate, toDate] = Array.isArray(timeClause.value) ? timeClause.value : [timeClause.value, null];
  const fromMoment: Moment = DateRange.convertAbsoluteDateToMoment(
    fromDate,
    projectTimezone,
    asOfDate,
    timePeriodSettings,
  );
  const toMoment: Moment = DateRange.convertAbsoluteDateToMoment(toDate, projectTimezone, asOfDate, timePeriodSettings);

  switch (timeClause.dateRangeType) {
    case DateRangeType.RelativeAfter: {
      if (SUB_DAY_TIME_UNITS.includes(timeClause.window.unit)) {
        // Moment diff uses floor to get the number of hours.
        // For consistency, we manually call `floor` since minute units can result in a fractional number of hours.
        return Math.floor(timeClause.window.value * NUMBER_OF_HOURS_IN_UNIT[timeClause.window.unit]);
      }

      const numFullDays: number = timeClause.window.value * NUMBER_OF_DAYS_IN_UNIT[timeClause.window.unit];
      const startOfDate: Moment = moment(asOfDate).startOf(`day`);
      return numFullDays * 24 + asOfDate.diff(startOfDate, `hours`);
    }
    case DateRangeType.Since:
      return asOfDate.diff(fromMoment, `hours`);
    case DateRangeType.Between:
      return asOfDate.isBefore(toMoment) ? asOfDate.diff(fromMoment, `hours`) : toMoment.diff(fromMoment, `hours`);
    case DateRangeType.On:
    default:
      // TODO(go/j/AE-495): Remove legacy time clause support and raise an error here instead.
      console.warn(`Unexpected legacy time clause format.`);
      return asOfDate.isBefore(toMoment) ? asOfDate.diff(fromMoment, `hours`) : toMoment.diff(fromMoment, `hours`);
  }
}

export function getNumberOfDaysInTimeClause(timeClause): number {
  const [fromDate, toDate] = Array.isArray(timeClause.value) ? timeClause.value : [null, null];
  switch (timeClause.dateRangeType) {
    case DateRangeType.RelativeAfter:
      return Math.ceil(timeClause.window.value * NUMBER_OF_DAYS_IN_UNIT[timeClause.window.unit]);
    case DateRangeType.Since:
      if (fromDate === START_OF_TODAY) {
        return 1;
      } else if (fromDate === START_OF_WEEK) {
        return NUMBER_OF_DAYS_IN_UNIT[TimeUnit.Week];
      } else if (fromDate === START_OF_MONTH) {
        return NUMBER_OF_DAYS_IN_UNIT[TimeUnit.Month];
      } else if (fromDate === START_OF_QUARTER) {
        return NUMBER_OF_DAYS_IN_UNIT[TimeUnit.Quarter];
      } else if (fromDate === START_OF_YEAR) {
        return NUMBER_OF_DAYS_IN_UNIT[TimeUnit.Year];
      }
      return moment().diff(moment(fromDate), `days`);
    case DateRangeType.On:
      return 1;
    case DateRangeType.Between:
      // We could use moment#startOf for start of days but the ranges
      // need to be adjusted for project timezone
      if (fromDate === START_OF_TODAY && toDate === END_OF_TODAY) {
        return 1;
      }
      if (fromDate === START_OF_YESTERDAY && toDate === END_OF_YESTERDAY) {
        return 1;
      }
      return moment(toDate).diff(moment(fromDate), `days`);
    default:
      if (timeClause.value === TODAY) {
        return 1;
      } else if (isNumber(timeClause.value)) {
        return Math.ceil(timeClause.value * NUMBER_OF_DAYS_IN_UNIT[timeClause.unit]);
      }
      return moment(toDate).diff(moment(fromDate), `days`);
  }
}

export class TimeClause {
  static TYPE: typeof ClauseType.Time = ClauseType.Time;

  static rangeToUnitValue(range): {unit: BaseTimeUnit; value: number | string} | EmptyObj {
    return RANGE_TO_VALUE_AND_UNIT[range] || {};
  }

  static unitValueToRange(unit, value): string | null {
    const unitVals = UNIT_AND_VALUE_TO_RANGE[unit];
    return (unitVals && unitVals[value]) || null;
  }

  declare dateRangeType: DateRangeType;
  declare unit: BaseTimeUnit;
  declare value: string[];
  declare rolling: boolean | null;
  declare window: RelativeDate;

  constructor(attrs: any = {}) {
    this.dateRangeType = attrs.dateRangeType;
    if (attrs.window) {
      this.window = attrs.window;
    }
    const {unit, value} = attrs;
    if (unit) {
      this.unit = unit;
    }
    if (value) {
      this.value = value;
    }
    if (attrs.rolling) {
      this.rolling = attrs.rolling;
    }
  }

  extend(attrs = {}) {
    return new TimeClause({...this.attrs, ...attrs});
  }

  isValidNumberValue(value) {
    return isNumber(value) && value >= 0;
  }

  isValidValue(value) {
    return [
      this.isValidNumberValue(value),
      value === TODAY,
      isDate(value),
      Array.isArray(value) &&
        value.length <= 2 &&
        value.length > 0 &&
        (value.every(isString) || value.every(this.isValidNumberValue) || value.every(isDate)),
    ].some(Boolean);
  }

  isValidRelativeDate(date: RelativeDate) {
    return isObject(date) && TIME_UNIT_LIST.includes(date.unit as BaseTimeUnit) && this.isValidNumberValue(date.value);
  }

  isValidCalendarDateArray(value) {
    return (
      this.isValidDateStringArray(value) ||
      (this.rolling && Array.isArray(value) && value.every((v) => this.isValidRelativeDate(v)))
    );
  }

  isValidDateStringArray(value) {
    return (
      Array.isArray(value) &&
      value.every(
        (v) =>
          (isString(v) &&
            (SPECIAL_RANGES_SET.has(v) || moment(v, CALENDAR_DATE_FORMAT, /* strict */ true).isValid())) ||
          moment(v, TIMESTAMP_FORMAT, /* strict */ true),
      )
    );
  }

  toBookmarkData() {
    return pickBy(this.attrs, (value) => !isUndefined(value));
  }

  toUrlData() {
    let keys = [];
    if (this.dateRangeType) {
      // new datepicker
      keys = [`dateRangeType`, `unit`].concat(
        this.dateRangeType === DATE_RANGE_TYPE.RELATIVE ? [`window`] : [`value`, `rolling`],
      );
    } else {
      // TODO: Can this codepath be removed?
      keys = [`value`, `unit`];
    }

    return pick(this, keys);
  }

  serialize() {
    return this.toUrlData();
  }

  get attrs() {
    return pick(this, `dateRangeType`, `window`, `unit`, `value`, `rolling`);
  }

  get valid() {
    const range = this.range;
    return [
      TIME_UNIT_LIST.includes(this.unit),
      this.dateRangeType !== DATE_RANGE_TYPE.RELATIVE || (this.window && this.isValidRelativeDate(this.window)),
      this.dateRangeType !== DATE_RANGE_TYPE.SINCE || range === RANGES.TODAY || this.isValidDateStringArray(this.value),
      this.dateRangeType !== DATE_RANGE_TYPE.ON || this.isValidCalendarDateArray(this.value),
      this.dateRangeType !== DATE_RANGE_TYPE.BETWEEN ||
        this.isValidCalendarDateArray(this.value) ||
        range === RANGES.YESTERDAY,
      this.dateRangeType || this.isValidValue(this.value),
    ].every(Boolean);
  }

  /**
   * Returns the number of days queried, for tracking purposes.
   */
  getNumberOfDaysQueried() {
    return getNumberOfDaysInTimeClause(this.attrs);
  }

  get range() {
    if (!this.dateRangeType) {
      return TimeClause.unitValueToRange(this.unit, this.value);
    }
    const entry = Object.entries(RANGE_TO_TIME_CLAUSE_PARAMS).find(([, {dateRangeType, window, unit, value}]) => {
      switch (dateRangeType) {
        case DATE_RANGE_TYPE.RELATIVE:
          return isEqual(this.window, window) && this.unit === unit;
        case DATE_RANGE_TYPE.SINCE:
          return !this.window && this.value === value;
        case DATE_RANGE_TYPE.BETWEEN:
          return isEqual(this.value, value);
        default:
          return false;
      }
    });
    return entry ? entry[0] : null;
  }
}
